// appPreview.jsx
//
// Η ζωντανή προεπισκόπηση της εφαρμογής Panellele, μοιρασμένη ανάμεσα στην
// ενότητα της αρχικής και στη σελίδα /app.
//
// ΓΙΑΤΙ ΕΙΝΑΙ ΖΩΝΤΑΝΗ ΚΑΙ ΟΧΙ ΣΤΙΓΜΙΟΤΥΠΟ
// Ένα screenshot θα έδειχνε «300 μέρες» για πάντα και θα γινόταν ψέμα μέσα σε
// μία μέρα. Αυτό εδώ είναι πραγματικό component με πραγματικά δεδομένα, στα
// χρώματα και στη γραμματοσειρά της εφαρμογής.
//
// Το σκούρο είναι το ΜΟΝΟ χρωματιστό πράγμα στη σελίδα. Το site είναι σκόπιμα
// ασπρόμαυρο· ένα έγχρωμο banner θα διαβαζόταν σαν διαφήμιση τρίτου.

/**
 * Η οθόνη «Και σήμερα».
 *
 * Οι στρογγυλεμένες γωνίες στα 2rem είναι σχήμα συσκευής, όχι επιλογή στυλ, γι'
 * αυτό βγαίνουν εκτός της κλίμακας του site.
 */
export function PhonePreview({ days, progress, size = 168 }) {
  const stroke = 9
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const center = size / 2

  const angle = -Math.PI / 2 + progress * 2 * Math.PI
  const capX = center + radius * Math.cos(angle)
  const capY = center + radius * Math.sin(angle)

  return (
    <div
      role="img"
      aria-label={`Η οθόνη της εφαρμογής: απομένουν ${days} μέρες για το πρώτο μάθημα, Νεοελληνική Γλώσσα, Παρασκευή 28 Μαΐου στις 08:30.`}
      className="rounded-[2rem] border border-gray-900/10 bg-[#0B0B0E] p-5 pb-6 shadow-xl shadow-gray-900/10 ring-1 ring-inset ring-white/5"
    >
      <div aria-hidden="true">
        <div className="flex items-baseline justify-between">
          <span className="text-[11px] font-medium text-[#F3F3F5]">Πανελλαδικές 2027</span>
          <span className="text-[11px] text-[#8E8E9A]">4ο πεδίο</span>
        </div>

        <div className="mt-5 flex justify-center">
          <div className="relative" style={{ width: size, height: size }}>
            <div
              className="absolute inset-0 rounded-full bg-[#17171B]"
              style={{ boxShadow: '0 10px 30px rgba(0,0,0,.45)' }}
            />
            <svg width={size} height={size} className="absolute inset-0">
              <circle
                cx={center}
                cy={center}
                r={radius - stroke / 2 - 8}
                stroke="#2A2A31"
                strokeWidth="1"
                fill="none"
              />
              <circle
                cx={center}
                cy={center}
                r={radius}
                stroke="#121216"
                strokeWidth={stroke}
                fill="none"
              />
              <circle
                cx={center}
                cy={center}
                r={radius}
                stroke="#8AA9FF"
                strokeWidth={stroke}
                strokeLinecap="round"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - progress)}
                transform={`rotate(-90 ${center} ${center})`}
              />
              <circle cx={capX} cy={capY} r={stroke / 2 + 2.5} fill="#8AA9FF" />
              <circle
                cx={capX}
                cy={capY}
                r={stroke / 2 + 2.5}
                stroke="#17171B"
                strokeWidth="2"
                fill="none"
              />
            </svg>

            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[10px] text-[#8E8E9A]">μέχρι το πρώτο</span>
              {/* Το prerender ψήνει τον αριθμό της μέρας του build και το
                  hydration τον διορθώνει στον σημερινό. */}
              <span
                suppressHydrationWarning
                className="pnl-numeral mt-1 text-[64px] leading-none text-[#F3F3F5]"
              >
                {days}
              </span>
              <span className="mt-1.5 text-[10px] font-medium text-[#8AA9FF]">μέρες</span>
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-col items-center">
          <span className="h-[3px] w-10 rounded-full bg-[#8AA9FF]" />
          <span className="pnl-condensed mt-2 text-[22px] leading-none text-[#F3F3F5]">
            Νεοελληνική
          </span>
          <span className="mt-1.5 text-[11px] text-[#8E8E9A]">Παρασκευή 28 Μαΐου · 08:30</span>
        </div>

        <div className="mt-5 border-t border-[#2A2A31] pt-3">
          <PreviewRow days={days} name="Νεοελληνική Γλώσσα" when="Παρ 28 Μαΐου · 08:30" />
          <div className="my-2 h-px bg-[#2A2A31]" />
          <PreviewRow days={days + 5} name="Μαθηματικά" when="Τετ 2 Ιουνίου · 08:30" />
        </div>
      </div>
    </div>
  )
}

function PreviewRow({ days, name, when }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex w-10 flex-shrink-0 flex-col items-center">
        <span suppressHydrationWarning className="pnl-numeral text-[19px] leading-none text-[#F3F3F5]">
          {days}
        </span>
        <span className="mt-0.5 text-[9px] text-[#8E8E9A]">μέρες</span>
      </div>
      <div className="min-w-0">
        <p className="truncate text-[12px] font-medium text-[#F3F3F5]">{name}</p>
        <p className="truncate text-[10px] text-[#8E8E9A]">{when}</p>
      </div>
    </div>
  )
}

/**
 * Μια πραγματική ειδοποίηση της εφαρμογής, στο ύφος που τη δείχνει το κινητό.
 * Δείχνει ακριβώς τι θα λάβει ο μαθητής, χωρίς να χρειάζεται στιγμιότυπο.
 */
export function NotificationPreview({ title, body, when }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#0B0B0E]">
          <span className="pnl-numeral text-[15px] leading-none text-[#8AA9FF]">Π</span>
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-[13px] font-semibold text-gray-900">{title}</p>
            <span className="flex-shrink-0 text-[11px] text-gray-500">{when}</span>
          </div>
          <p className="mt-0.5 text-[12px] leading-snug text-gray-600">{body}</p>
        </div>
      </div>
    </div>
  )
}
